import { omit, pick as _pick, isEqual, cloneDeep } from 'lodash';
import {
  ITextStyle,
  TextStyle,
  Sprite,
  Container,
  SCALE_MODES,
  Graphics,
  Rectangle,
} from 'pixi.js';
import { BlurFilter } from '@pixi/filter-blur';
import { ResourceData } from '../../../../elements/resource/resource.types';
import {
  AlignParameters,
  AnimationItem,
  AnimationKeys,
  CellAlignment,
  Coords,
  FontAlign,
  FontWeight,
  IWAnimationMetaData,
  NewTextAnimation,
  Scale,
  ShapeConfig,
  ShapeSelectParams,
  ShowHideAnimation,
  TextAnimationConfig,
  TextAnimationValue,
  TextConfig,
  TextShapeDescriptor,
  Translate,
  TypeDef,
} from '../../../../elements/resource/types/shape.type';
import { RectangleElement } from '../primitive/rectangle-element';
import {
  RCPoint,
  RectangleController,
  RectangleDragResponse,
} from '../../../../elements/util/rectangle-controller/rectangle-controller';
import { ShapeService } from '../../shape.service';
import { selectTextDescriptorByIRI } from '../../../store/selector/editor.selector';
import { PrimitiveShape } from '../primitive/primitive-shape';
import { SVG } from '../primitive/svg-element';
import { addAnimationItemAction } from '../../../animation/store/animation.actions';
import { filter } from 'rxjs/operators';
import { setDescriptorValue } from '../../../store/editor.crud.actions';
import { TextMetrics, Texture } from 'pixi.js';
import { PathElement } from '../primitive/path-element';
import { Subscription } from 'rxjs';
import { TextLetter } from './text-letter';
import { _screenX, _screenY } from '../../../../services/canvas/canvas.service';
import {
  AutoAnimationFrame,
  BaseAnimationFrame,
  ShapeAnimationState,
} from '../../../animation/components/animation-frame/animation.types';
import { IncrementController } from '../../../animation/frame/increment/controller/increment.controller';
import { RootShape } from '../general/root/root-shape';
import { ContainerShape } from '../container/container.shape';
import { TextLine } from './text-line';
import { TextWord } from './text-word';
import gsap from 'gsap';
import { TextAnimationStep } from '../../../animation/text-animation.service';
import { FileRootShape } from '../general/root/file-root-shape';

export interface CharPosition {
  char: string;
  x: number;
  y: number;
}

export type DropShadowOpts = {
  color?: string; // hex like '#000000'
  alpha?: number; // 0..1
  blur?: number; // px blur in canvas space
  offset?: { x: number; y: number }; // px
  padding?: number; // extra shadow spread around bounds
  radius?: number; // corner radius of the box
  quality?: number; // oversampling multiplier, e.g. 2
  parent?: Container; // where to insert the shadow; default container.parent
  width?: number;
  height?: number;
};
export const RC_PADDING = 0;

export class TextShape extends PrimitiveShape<SVG, TextShapeDescriptor> {
  rect: RectangleElement;
  maskRectangle: RectangleElement;
  elementLevel: 'line' | 'word' | 'letter';
  // -- //
  getType() {
    return 'text-shape';
  }
  reset() {
    this.refreshElement();
    this.container.scale.x = 1;
    this.container.scale.y = 1;
    this.currentSegment = null;
    this.container.alpha = 1;

    this.reAlignInCell();
    this.lines.forEach(line => line.reset());
    this.words.map(word => {
      word.container.alpha = this.textConfig.opacity || 1;
    });
  }
  get textAnimationService() {
    return this.service.animationService.textAnimationService;
  }
  get letters(): TextLetter[] {
    return this.words.reduce((arr, word) => [...arr, ...word.letters], []);
  }
  get newLetters(): TextLetter[] {
    return this.words.reduce((arr, word) => [...arr, ...word.letters], []);
  }
  get words(): TextWord[] {
    return this.lines.reduce((arr, line) => {
      arr.push(...line.words);
      return arr;
    }, []);
  }
  get multipleLettersSelected() {
    return (
      this.selectionStartIndex != null &&
      this.selectionStartIndex !== this.currentSelectionIndex
    );
  }
  get textValue(): string {
    if (!this.text) {
      return '';
    }
    const text = this.text;
    if (text.startsWith('$.')) {
      const varName = text.split('.')[1];
      return this.parent.env?.[varName] || text;
    }
    return this.text;
  }

  get dim(): Coords {
    const width = this.wordWrap ? this.wordWrapWidth : this.baseWidth;

    if (this.lineHeight < 1) {
      return [
        width,
        this.lineHeight * this.baseLineHeight * (this.lines.length - 1) +
          this.baseLineHeight,
      ];
    }

    return [
      width,
      (this.lineHeight || 1) * this.baseLineHeight * this.lines.length,
    ];
  }

  save() {
    this.saveTranslate();
  }

  getBounds() {
    return this.container.getBounds();
  }

  get text() {
    return this.textConfig.text;
  }

  set text(value: string) {
    this.textConfig.text = value;
  }

  textConfig: TextConfig;
  get fontSize() {
    return this.textConfig.fontSize;
  }

  set fontSize(value: number) {
    this.textConfig.fontSize = value;
  }

  get wordWrap() {
    return this.textConfig.wordWrap;
  }

  set wordWrap(value: boolean) {
    this.textConfig.wordWrap = value;
  }

  get fontFamily() {
    return this.textConfig.fontFamily;
  }

  set fontFamily(value: string) {
    this.textConfig.fontFamily = value;
  }

  get lineHeight() {
    return 1.25;
  }

  set lineHeight(value: number) {
    this.textConfig.lineHeight = value;
  }

  get wordWrapWidth() {
    return this.textConfig.wordWrapWidth;
  }

  set wordWrapWidth(value: number) {
    this.textConfig.wordWrapWidth = value;
  }

  get letterSpacing() {
    return this.textConfig.letterSpacing || 0;
  }

  set letterSpacing(value: number) {
    this.textConfig.letterSpacing = value;
  }

  get fontStyle() {
    return this.textConfig.fontStyle || 'normal';
  }

  set fontStyle(value: 'normal' | 'italic' | 'oblique') {
    this.textConfig.fontStyle = value;
  }

  get align() {
    return this.textConfig.align || 'left';
  }

  set align(value: FontAlign) {
    this.textConfig.align = value;
  }

  get fontWeight() {
    return this.textConfig.fontWeight || 'normal';
  }

  set fontWeight(value: FontWeight) {
    this.textConfig.fontWeight = value;
  }

  get containerForRc() {
    return this.container;
  }

  get hasFrameRectangle() {
    return true;
  }

  get inputs(): TypeDef {
    return [
      ...super.inputs,
      {
        key: 'text',
        value: 'string',
      },
      {
        key: 'fontSize',
        value: 'string',
      },

      {
        key: 'fontStyle',
        value: 'string',
        options: ['normal', 'italic', 'oblique'],
      },
      {
        key: 'fontWeight',
        value: 'string',
        options: ['normal', 'bold', 'bolder', 'lighter'],
      },
    ];
  }

  get env() {
    return {
      ...super.env,
      ...{
        text: this.textValue || '',
        fontSize: this.fontSize || '',
        fontStyle: this.fontStyle || '',
        fontWeight: this.fontWeight || '',
      },
    };
  }
  arrowLeftSub: Subscription;
  arrowRightSub: Subscription;
  arrowDownSub: Subscription;
  arrowUpSub: Subscription;
  cursorIndex: string;
  selectionStartIndex: number = null;
  currentSelectionIndex: number = null;
  get currentLetter() {
    return this.newLetters[this.currentSelectionIndex];
  }
  selectionEndIndex: number = null;
  constructor(
    service: ShapeService,
    data: ResourceData<TextShapeDescriptor>,
    config: ShapeConfig,
  ) {
    super(service, data, config);

    this.elementLevel = this.config?.wordDisplayLevel || 'word';

    // if (
    //   JSON.stringify(
    //     (this.parent as FileRootShape).descriptor?.textAnimationStep || {},
    //   ).includes('dropShadow')
    // ) {
    //   this.elementLevel = 'word';
    // }

    // start with the

    // -- // -- // -- //

    this.textConfig =
      this.descriptor.textConfig ||
      _pick(this.descriptor, [
        'text',
        'fontSize',
        'align',
        'fontStyle',
        'fontWeight',
        'wordWrap',
        'wordWrapWidth',
        'letterSpacing',
        'fontFamily',
      ]);

    this.service.fontService.registerTextShape(this);

    this.cs.generalEventSubscribe('fitcanvas', () => {
      if (!this.container) {
        return;
      }
      const { width, height } = this.getBounds();
      // this.baseWidth = width;
      // / height;
      // if (this.text.length > 1000) {
      // console.log('fitcanvas', width, height, 'text', this.text);
      // }
    });

    if (this.config?.isRoot) {
      this.store
        .select(selectTextDescriptorByIRI(this.IRI))
        .pipe(filter(val => !!val))
        .subscribe(async textConfig => {
          if (isEqual(textConfig, this.textConfig)) {
            return;
          }

          await this.service.fontService._loadFont(textConfig.fontFamily);
          this.textConfig = cloneDeep(textConfig);
          setTimeout(() => {
            this.refreshElement();
          }, 100);
        });
    }

    this.keyEventSubscribe('t+a', () => {
      if (!this.selected) {
        return;
      }
      this.store.dispatch(
        addAnimationItemAction({
          IRI: this.IRI,
          item: {
            key: 'type-animation',
            value: true,
          },
        }),
      );
    });
    this.init();
    if (this.descriptor.hidden) {
      this._hide();
    }

    if (this.config?.isRoot) {
      this.initArrowSubs();
    }

    this.cs.registerKeyDownHandler(key => this.keyDownHandler(key));
  }

  initArrowSubs() {
    // this.arrowRightSub = this.cs.keyDownEventSubscribe('ArrowRight', () => {
    //   this.cursorRightHandler();
    // });
    // this.arrowLeftSub = this.cs.keyDownEventSubscribe('ArrowLeft', () => {
    //   this.cursorLeftHandler();
    // });
    // this.arrowUpSub = this.cs.keyDownEventSubscribe('ArrowUp', () => {
    //   this.cursorUpHandler();
    // });
    // this.arrowDownSub = this.cs.keyDownEventSubscribe('ArrowDown', () => {
    //   this.cursorDownHandler();
    // });
  }

  getAppearAnimation(): BaseAnimationFrame {
    return {
      type: 'base',
      action: 'appear',
      targetShape: this.IRI,
    };
  }

  getAutoAnimations(): AutoAnimationFrame[] {
    // -- // -- //
    return [];
  }

  // async animate() {
  //   // -- // -- // -- //
  //   this.words.map(word => word.hide());
  //   const noSpaces = this.words.filter(word => word.text == ' ');

  //   for (let i = 0; i < noSpaces.length; i++) {
  //     await this.timeout(400);
  //     noSpaces[i].show();
  //   }
  // }

  async timeout(delay: number) {
    await new Promise(res => setTimeout(res, delay));
  }

  applyScale(scale: Scale) {
    const { x, y } = scale;
    this.container.scale.set(x as number, y as number);
  }

  startScaleAppearAnimation(scale: 'shrink' | 'grow', division) {
    // -- // -- //
    // -- // -- //
    this.scaleXIncrementController = new IncrementController(0, 1, division);
    this.scaleYIncrementController = new IncrementController(0, 1, division);

    // -- // -- //
    this.translateXIncrementController = new IncrementController(
      this.x + this.maxLineWidth / 2,
      this.x,
      division,
    );
    this.translateYIncrementController = new IncrementController(
      this.y + this.lineHeight / 2,
      this.y,
      division,
    );
  }

  get keys() {
    return ['text', 'fontSize', 'fontStyle', 'fontWeight'];
  }

  applyTranslate(translate: Translate, setTranslate?: boolean): void {
    super.applyTranslate(translate, setTranslate);
    if (this.text == 'Just describe the content') {
      // console.log('applyTranslate', translate);
      // console.log('hey');
    }
  }
  widthRatio: number;
  doAlign(alignment: CellAlignment, params: AlignParameters = {}) {
    super.doAlign(alignment, params);

    if (!alignment) {
      return;
    }
    // console.log('text-shape > doAlign', alignment); // -- //

    const { w, widthRatio: initialWidthRatio } = alignment;

    let widthRatio = initialWidthRatio;

    if (this.descriptor.textConfig.widthRatio) {
      widthRatio = this.descriptor.textConfig.widthRatio;
    }

    this.baseWidth = w * widthRatio;

    this.widthRatio = widthRatio;

    // if (!this.textConfig.fontSize) {

    if (!this.textConfig.fontSize || this.textConfig.fontSize == 28) {
      this.setFontSizeByAlignment();
    }
    // }

    /* console.log('-----------------------------------------------------');
    console.log('font-size', this.fontSize); */
    // const { save } = params;
    // if (save) {
    //   this.saveKey('fontSize', this.fontSize);
    // }
    this.baseHeight = this.fontSize * 1.25 * this.lines.length;
    const { height: _h } = this.container.getBounds();

    this.refreshElement();
    return this.alignInCell(alignment.w, alignment.h);
  }

  setFontSizeByAlignment(): Coords {
    // console.log('setFontsizeByAlignment', this.cellAlignment);

    const {
      w,
      h,
      margin,
      align,
      widthRatio: initialWidthRatio,
      heightRatio,
    } = this.cellAlignment;
    // this.fontSize = fontSize;
    // this.refreshElement();
    // const _margin = margin || align == 'center' ? 0.3 : 0.1;
    const _margin = 0.05;
    const [xMargin, yMargin] = [w, h].map(val => val * _margin);
    let [width, height] = [w - 2 * xMargin, h - 2 * yMargin];

    let widthRatio = initialWidthRatio;

    if (this.descriptor.textConfig.widthRatio) {
      widthRatio = this.descriptor.textConfig.widthRatio;
    }
    if (this.text == 'Meet') {
      widthRatio = 0.4;
    }
    width = width * (widthRatio || 1);
    height = height * (heightRatio || 1);

    // -- //

    let {
      width: actualWidth,
      height: actualHeight,
      // eslint-disable-next-line
      widestLine,
    } = this.getDimensions();

    // let { maxLineWidth: actualWidth, lineHeight: actualHeight } =
    //   this.measureTextMetric(this.text, this.elementStyle);

    let metric;
    if (actualWidth > width || actualHeight > height) {
      while (actualWidth > width || actualHeight > height) {
        this.fontSize--;

        metric = this.measureTextMetric(widestLine, this.elementStyle);

        actualWidth = metric.maxLineWidth;
        actualHeight = metric.lineHeight;
      }
    } else {
      // scale up to fit
      while (actualWidth < width && actualHeight < height) {
        this.fontSize++;
        // this.refreshElement();
        /* console.log('---- 2 -------', {
          actualWidth,
          width,
          actualHeight,
          height,
          fontSize: this.fontSize,
          length: widestLine.length,
        }); */
        // console.log('fontSize', this.fontSize, 'actualWidth', actualWidth);
        metric = this.measureTextMetric(widestLine, this.elementStyle);
        actualWidth = metric.maxLineWidth;
        actualHeight = metric.lineHeight;
      }
    }

    // -- // -- //
    // this.baseHeight = metric.lineHeight;

    return [actualWidth, actualHeight];
  }

  detectAutoFontSize(
    container?: { w?: number; h?: number },
    ratio = 0.7,
  ): number {
    const w =
      container?.w ?? this.parent?.cellAlignment?.w ?? this.cellAlignment?.w;
    const h =
      container?.h ?? this.parent?.cellAlignment?.h ?? this.cellAlignment?.h;

    // If no container info available, keep current size
    if (!w && !h) {
      return this.fontSize || 16;
    }

    // Start from at least 1px
    let size = 5;
    let lastGood = Math.max(1, size);

    // Try increasing until we exceed the target ratio; step by 1px for determinism
    // Use measureTextMetric to get width/height for the sample text
    // Guard for runaway loop
    // console.log('w', w, 'h', h, 'ratio', ratio);
    // -- // -- //

    for (let i = 0; i < 2000; i++) {
      const testStyle = { ...this.elementStyle, fontSize: size } as ITextStyle;
      const metric = this.measureTextMetric(this.text, testStyle);

      const fitsW = w ? metric.maxLineWidth <= ratio * w : true;
      const fitsH = h ? metric.lineHeight <= ratio * h : true;

      if (fitsW && fitsH) {
        lastGood = size;
        size += 1;
        continue;
      }
      break;
    }

    return lastGood;
  }

  setAutoFontSize(ratio = 0.7) {
    const w = this.parent?.cellAlignment?.w ?? this.cellAlignment?.w;
    const h = this.parent?.cellAlignment?.h ?? this.cellAlignment?.h;
    const best = this.detectAutoFontSize({ w, h }, ratio);
    this.fontSize = best;
    this.refreshElement();
    return best;
  }

  _alignInCell(actualWidth: number, actualHeight: number, noApply?: boolean) {
    const { w, h, x, y, margin, align } = this.cellAlignment;

    console.log('alignInCell', {
      actualHeight,
      actualWidth,
      w,
      h,
      // cellAlignment: this.cellAlignment //
    });

    this.baseLineHeight = actualHeight;
    this.maxLineWidth = actualWidth;

    let newX = x;
    const newY = y + (h - this.baseLineHeight) / 2;

    if (!align || align == 'center') {
      const width = this.wordWrapWidth || this.maxLineWidth;
      newX = x + (w - width) / 2;
    }

    if (noApply) {
      return {
        x: newX,
        y: newY,
      };
    }

    this.translate.x = newX;
    this.translate.y = newY;

    this.applyTranslate(this.translate);
    return this.translate;
  }

  gsapAnimateNew(animation: NewTextAnimation) {
    return gsap.timeline();
  }

  gsapAnimateStep(
    animation: TextAnimationStep,
    baseTimeline?: gsap.core.Timeline,
    finished?: () => void,
  ) {
    // return this.lines[0]?.gsapAnimateNew(animation) || gsap.timeline(); //
    // -- // -- // -- // -- //

    const {
      type: baseType,
      line,
      word,
      duration: initialDuration,
      delay,
      next,
      paralell,
      ease,
      zoom,
      translate,
      overlap,
      forceFade,
      effects,
      forEach,
      letter,
      target,
    } = animation;

    if (animation.forEach) {
      return (this.parent as FileRootShape).gsapAnimateStep(animation);
    }
    const duration =
      baseType == 'disappear' ? initialDuration || 0.2 : initialDuration;
    const timeline = gsap.timeline();
    const onStartFcns: Array<() => void> = [];

    // const effects = {
    //   dropShadow: {
    //     target: 'letter',
    //     range: 5,
    //   },
    //   colorize: {
    //     target: 'letter',
    //     range: 5,
    //   },
    // };

    if (letter) {
      const { type } = letter;

      const lettertimeline = gsap.timeline();

      // -- //

      console.log('letter -> input', letter);

      this.elementLevel = 'letter';
      this.words.map(word => {
        word.container.alpha = 0;
      });
      this.refreshElement();
      this.letters.map(letter => (letter.element._element.alpha = 0));
      const length = this.letters.length;
      const { d, gap } = this.animationService.makeOverlapTiming(
        length,
        2,
        0.95,
      );
      this.letters.map((letter, index) => {
        lettertimeline.add(
          gsap.to(letter.element._element, {
            duration: d,
            alpha: 1,
          }),
          index * gap,
        );
      });

      timeline.add(lettertimeline, 0);
    }

    if (effects?.rowAppear) {
      // Align all line in a row.

      const parent = this.parent as FileRootShape;

      const [w, h] = [_screenX, _screenY].map(
        val => val * parent.rootContainer.scale.x,
      );

      parent.rootContainer.position.y += 130;
      parent.rootContainer.position.x -= 300;
      parent.rootContainer.scale.x *= 1.1;
      parent.rootContainer.scale.y *= 1.1;

      const [_w, _h] = [_screenX, _screenY].map(
        val => val * parent.rootContainer.scale.x,
      );

      parent.rootContainer.position.x += (w - _w) / 2;
      parent.rootContainer.position.y += (h - _h) / 2;

      // -- //

      // console.log('lineHeight ---------', this.lineHeight);
      // this.lines.slice(1).map(line => (line.container.alpha = 0));
      const newY = (this.parentHeight - this.baseLineHeight) / 2;
      this.container.position.y = newY;

      const [line] = this.lines;
      // -- // -- //
      const startLineIndex = 1;
      this.container.position.x = this.parentWidth / 2 + 100;

      const rowAppearTimeline = gsap.timeline();
      let _offset = 0;
      this.lines.map((line, i) => {
        // console.log('line.width', line.width);
        if (i < startLineIndex) {
          _offset += line.width + this.spaceWidth;
          line.container.alpha = 0.5;
        } else if (i == startLineIndex) {
          _offset += line.width / 2;
        } else {
          line.container.alpha = 0.5;
        }
      });

      this.container.position.x -= _offset;

      // rowAppearTimeline.add(
      //   gsap.to(this.container.position, {
      //     x: (i, target) => {
      //       return target.x;
      //     },
      //     duration: 3,
      //     // alpha: 1,
      //     ease: 'power1.inOut',
      //   }),
      // );
      let position = 0;
      this.lines.map((line, i) => {
        line.container.position.x = position;
        line.container.position.y = 0;
        const dx = line.width + this.spaceWidth;

        const next = this.lines[i + 1];
        // -- // -- //
        position += dx;

        const offset = 2 * this.spaceWidth;

        if (i < startLineIndex) {
          return;
        }

        rowAppearTimeline.add(
          gsap.to(this.container.position, {
            duration: 0.3,
            // x: (i, target) => {
            //   return target.x - line.width - offset;
            // },
            x: `-=${line.width / 2 + this.spaceWidth + (next ? next.width / 2 : line.width / 2) - offset}`,
            onStart: () => {
              setTimeout(() => {
                line.container.alpha = 0.5;
                if (next) {
                  next.container.alpha = 1;
                }
              }, 150);
            },
            alpha: 1,
            ease: 'power1.inOut',
          }),
        );

        // wait phase
        rowAppearTimeline.add(
          gsap.to(this.container.position, {
            duration: 0.6,
            // x: (i, target) => {
            //   return target.x - this.spaceWidth;
            // },
            x: `-=${offset}`,
            alpha: 1,
            ease: 'none',
          }),
        );

        // -- //
      });

      timeline.add(rowAppearTimeline);

      return timeline;
      // -- //
    }

    if (!word && !line?.translate) {
      // -- //
      // TODOx
      // if (baseType == 'appear') {
      //   this.container.alpha = 0;
      // }
      // if (baseType == 'appear' || baseType == 'disappear') {
      //   timeline.add(
      //     gsap.to(this.container, {
      //       duration: duration || 1,
      //       alpha: baseType == 'disappear' ? 0 : 1,
      //       // ease: 'power1.inOut',
      //     }),
      //     0,
      //   );
      // }
    }

    if (forceFade) {
      // TODOx
      if (baseType == 'appear') {
        this.container.alpha = 0;
      }
      if (baseType == 'appear' || baseType == 'disappear') {
        timeline.add(
          gsap.to(this.container, {
            duration: duration || 1,
            alpha: baseType == 'disappear' ? 0 : 1,
            // ease: 'power1.inOut',
          }),
          0,
        );
      }
    }

    if (baseType == 'effect') {
      // Blink effect (container-level) when no explicit line target is provided
      const blink =
        (animation as any).effect?.blink || animation.effects?.blink;
      if (blink && !line) {
        const cnt = Math.max(1, +blink.cnt || 1);
        const total = duration || 1;
        const half = total / cnt / 2;
        for (let i = 0; i < cnt; i++) {
          timeline.add(
            gsap.to(this.container, {
              duration: half,
              alpha: 0.2,
              ease: 'power1.inOut',
            }),
            i * 2 * half,
          );
          timeline.add(
            gsap.to(this.container, {
              duration: half,
              alpha: 1,
              ease: 'power1.inOut',
            }),
            i * 2 * half + half,
          );
        }
      }

      const dropShadow = animation.effects?.dropShadow;
      console.log('dropShadow', dropShadow);
      if (dropShadow) {
        const word = this.words.find(w => w.text == dropShadow.target);
        console.log('word', word);
        if (word) {
          // return timeline;
          const wordTimeline = gsap.timeline();

          word.textElement.hide();
          word.letters.map((letter, index) => {
            letter.initElement();
            wordTimeline.add(
              gsap.to(letter.element.element.style, {
                onStart: () => {
                  // const gloveRect = new GloveRectElement(
                  //   {
                  //     color: '#e386d4ff',
                  //     alpha: 1,
                  //     center: [0, 0],
                  //     radius: [word.width / 4, this.baseLineHeight / 4],
                  //   },
                  //   // word.container,
                  // );
                  const bounds = word.textElement.element.getLocalBounds();
                  const padding = 4;

                  const bg = new Graphics();
                  bg.beginFill(0xffff00, 0.8); // yellow-ish, semi-transparent
                  bg.drawRoundedRect(
                    bounds.x - padding,
                    bounds.y - padding,
                    bounds.width + padding * 2,
                    bounds.height + padding * 2,
                    4,
                  );
                  bg.endFill();

                  // 3) add a slight blur to the background only
                  bg.filters = [new BlurFilter(4)];

                  word.container.addChildAt(bg, 0);

                  bg.filterArea = new Rectangle(
                    bounds.x - padding * 2,
                    bounds.y - padding * 2,
                    bounds.width + padding * 4,
                    bounds.height + padding * 4,
                  );
                  // letter.element.element.style.dropShadow = true;
                  // letter.element.element.style.dropShadowColor = '#e386d4ff';
                  // letter.element.element.style.dropShadowBlur = 0;
                },
                dropShadowBlur: 20,
                duration: 0.5,
              }),
              index * 0.1,
            );
          });
          timeline.add(wordTimeline, 0);
        }
        // const letterDuration = (duration || 1) / this.letters.length;
        // const textLength = this.letters.reduce(
        //   (acc, word) => acc + word.width,
        //   0,
        // );
        // const avgLetterWidth = textLength / this.letters.length;
        // const [firstLineRef] = this.lines;
        // const firstWord = firstLineRef.words[firstLineRef.words.length - 2];
        // const sprite = firstWord._addColorGradient({
        //   baseColor: '#ffffff',
        //   targetColor: '#ff7ea9ff',
        //   textWidth: firstWord.width,
        //   gradientWidth: 4 * avgLetterWidth,
        //   letterWidth: avgLetterWidth,
        // });
        // timeline.add(
        //   gsap.to(sprite, {
        //     x: 0,
        //     duration: duration || 1,
        //     onStart: () => {
        //       firstWord.setSpriteMask();
        //     },
        //   }),
        //   0,
        // );
      }
    }

    if (line) {
      const {
        type,
        translate,
        ease: lineEase,
        eases: lineEases,
        alternate,
        segmentHighlight,
        textHide,
      } = line;

      if (segmentHighlight) {
        const { lineOverlap, wordOverlap, color, texts, target, range, blur } =
          segmentHighlight;

        const segmentHightlightTimeline = gsap.timeline();

        const innerDuration = (animation.duration || 1) / this.lines.length;
        const lines = this.lines.filter((line, index) => texts[index]);

        // console.log('lines', lines);
        const { d, gap } = this.animationService.makeOverlapTiming(
          lines.length,
          4.2,
          0.5,
        );

        // console.log('segmentHighlight', { d, gap });
        const _texts = texts.filter(text => text);
        lines.map((line, index) => {
          const lineAnimation = line.gsapSegmentHighlight(
            _texts[index],
            d,
            index == lines.length - 1,
          );
          segmentHightlightTimeline.add(lineAnimation, index * gap);
        });

        let start: number;
        /* segmentHightlightTimeline.eventCallback('onStart', () => {
          console.log('on-start > segmentHightlightTimeline');
          start = performance.now();
        });
        segmentHightlightTimeline.eventCallback('onComplete', () => {
          console.log(
            'on-complete > segmentHightlightTimeline',
            performance.now() - start,
          );
        }); */

        timeline.add(segmentHightlightTimeline, 0.5);
        // -- // -- //
      }

      if (textHide) {
        const { except } = textHide;
        const wordsToHide = this.words.filter(
          word => !except.includes(word.text),
        );
        timeline.add(
          gsap.to(
            wordsToHide.map(word => word.container),
            {
              alpha: 0,
              duration: 0.4,
            },
          ),
          0,
        );
      }

      // TODO - move-back
      if (!translate && !segmentHighlight) {
        const [firstLine] = this.lines;
        const { timeline: segmentTimeLine, onStartFcns: lineOnStartFcns } =
          firstLine.gsapAnimate(animation);
        onStartFcns.push(...lineOnStartFcns);
        timeline.add(segmentTimeLine, 0);
      }

      if (translate) {
        const {
          from,
          to,
          duration: lineTranslateDuration,
          overlap: lineTranslateOverlap,
        } = translate;
        if (from || to) {
          this.lines.map((line, index) => {
            const duration = lineTranslateDuration || initialDuration || 1;
            const n = this.lines.length;
            let lineDuration = (duration || 1) / n;
            if (lineTranslateOverlap) {
              lineDuration =
                duration / ((1 - lineTranslateOverlap) * (n - 1) + 1);
            }

            const { timeline: lineTimeline } = line.gsapAnimate({
              ...animation,
              duration: lineDuration,
            });
            timeline.add(
              lineTimeline,
              index * lineDuration * (1 - (lineTranslateOverlap || 0)),
            );

            // if (baseType == 'disappear' || baseType == 'appear') {
            //   timeline.add(
            //     gsap.to(line.container, {
            //       duration: lineDuration,
            //       alpha: baseType == 'disappear' ? 0 : 1,
            //       ease: 'power1.inOut',
            //     }),
            //     index * lineDuration * (1 - (lineTranslateOverlap || 0)),
            //   );
            // }

            // if (from) {
            //   timeline.add(
            //     gsap.to(line.container.position, {
            //       duration: lineDuration,
            //       x: (i, target) => {
            //         return target.x - x;
            //       },
            //       y: (i, target) => {
            //         return target.y - y;
            //       },
            //       ease: lineEases?.[index] || lineEase || 'power1.inOut',
            //     }),
            //     index * lineDuration * (1 - (lineTranslateOverlap || 0)),
            //   );
            // }

            // if (to) {
            //   timeline.add(
            //     gsap.to(line.container.position, {
            //       duration: lineDuration,
            //       x: (i, target) => {
            //         return target.x + xTo;
            //       },
            //       y: (i, target) => {
            //         return target.y + yTo;
            //       },
            //       ease: lineEases?.[index] || lineEase || 'power1.inOut',
            //     }),
            //     index * lineDuration * (1 - (lineTranslateOverlap || 0)),
            //   );
            // }
          });
          // this.container.position.x += x;
          // this.container.position.y += y;
        }
      }

      if (type) {
        console.log('type', type);
        const { timeline: lineTimeline } = this.lines[0].gsapAnimate(animation);
        // -- // -- // -- //
        timeline.add(lineTimeline, 0);
        // timeline.add(
        //   gsap.to(this.container, {
        //     alpha: 0,
        //     duration: 1,
        //     ease: 'power1.inOut',
        //   }),
        //   0,
        // );
      }
    }

    if (zoom) {
      const { from, to, duration: zoomDuration, translate } = zoom;
      const [zoomDx, zoomDy] = translate || [0, 0];

      // const [dx, dy] = [0, 0];
      // const width = this.maxLineWidth;
      // const height = this.baseLineHeight;
      // -- // -- // -- //
      // const baseBounds = this.container.getBounds();

      const { width, height } = {
        width: this.currentWidth,
        // width: baseBounds.width / this.cs.fitScale,
        height: this.baseHeight,
      };
      // const [_width, _height] = [this.baseWidth, this.baseHeight];

      if (from) {
        // console.log('bounds', this.container.getBounds()); //
        // console.log('bounds', this.container.width, this.container.height); //
        onStartFcns.push(() => {
          // -- //

          // Old approach
          // const fromScale = from * this.segmentToOne;
          const fromScale = from;
          const previousWidth = this.baseWidth;
          // Old approach
          // const nextWidth = this.currentWidth * fromScale;
          const nextWidth = this.baseWidth * fromScale;

          const __dx = (previousWidth - nextWidth) / 2;
          const __dy = (this.baseHeight - this.baseHeight * fromScale) / 2;
          // old
          // const __dx = (width - from * width) / 2;
          // const __dy = (height - from * height) / 2;

          /* console.log('------ from ------', {
            from,
            fromScale,
            previousWidth,
            nextWidth,
            __dx,
            __dy,
          }); */
          this.container.scale.x = fromScale;
          this.container.scale.y = fromScale;

          this.container.position.x += __dx;
          this.container.position.y += __dy;

          // console.log('------ to ------', {
          //   to,
          //   segmentToOne: this.segmentToOne,
          // });
        });
      }

      // timeline.add(
      //   gsap.to(this.container, {
      //     alpha: 0.4,
      //     duration: 1,
      //     ease: 'power1.inOut',
      //   }),
      //   0,
      // );

      // timeline.eventCallback('onComplete', () => {
      //   // console.log('on-complete', this.container.scale.x);
      //   // this.bgService.render();
      //   finished?.();
      //   this.previousWidth = this.nextWidth;
      // });

      // If you want to include your fitCanvas / canvas zoom as well,
      // inject CanvasService and do:
      // const getGlobalScale = () => this.container.scale.x * this.cs.fitScale;

      // This tween is your "visual blur stays same while zooming" logic:
      // const keepBlurConstant = () => {
      //   const s = getGlobalScale();
      //   console.log('keepBlurConstant', s);
      //   if (s > 0) {
      //     (blur as any).strength = 10 / s;
      //   }
      // };

      if (to !== undefined) {
        const changes = [];
        timeline.add(
          gsap.to(this.container.scale, {
            duration: zoomDuration || duration || 1,
            x: to,
            y: to,
          }),
          0,
        );
        timeline.add(
          gsap.to(this.container.position, {
            duration: zoomDuration || duration || 1,
            x: (i, target) => {
              // Original
              const targetWidth = this.baseWidth * to;
              const currentWidth = this.baseWidth * this.container.scale.x;
              const dx = (targetWidth - currentWidth) / 2;
              return target.x - dx + zoomDx;
            },
            y: (i, target) => {
              const targetHeight = this.baseHeight * to;
              const currentHeight = this.baseHeight * this.container.scale.y;
              return target.y - (targetHeight - currentHeight) / 2 + zoomDy;
            },
            // ease: 'linear',
            // ease: 'power1.out',
          }),
          0,
        );
      }
    }

    if (translate) {
      const { from, to, type } = translate;

      const isRelative = !type || type == 'relative';

      if (isRelative) {
        if (from) {
          const [x, y] = from;
          this.container.position.x += x;
          this.container.position.y += y;
        }

        const [dx, dy] = to || from.map(v => -v);
        timeline.add(
          gsap.to(this.container.position, {
            duration: duration || 1,
            x: (i, target) => {
              return target.x + dx;
            },
            y: (i, target) => {
              return target.y + dy;
            },
            // ease: 'power1.inOut', // -- // -- // -- //
          }),
          0,
        );
      } else {
        if (from) {
          const [x, y] = from;
          this.container.position.x = x;
          this.container.position.y = y;
        }

        if (to) {
          const [x, y] = to;
          timeline.add(
            gsap.to(this.container.position, {
              duration: duration || 1,
              x,
              y,
              // ease: 'power1.inOut',
            }),
            0,
          );
        }
      }
    }

    timeline.eventCallback('onStart', () => {
      onStartFcns.map(fcn => fcn());
    });

    super.gsapAnimateStep(animation, timeline);
    // timeline.add(superTimeline, 0);

    // -- // -- //

    if (next) {
      const nextTimeline = this.gsapAnimateStep(next);
      // -- //
      if (next.overlap) {
        timeline.add(nextTimeline, `-=${next.overlap}`);
      } else {
        timeline.add(nextTimeline, `+=${next.delay || 0}`);
      }
    }

    if (paralell) {
      const _paralell = Array.isArray(paralell) ? paralell : [paralell];
      for (const p of _paralell) {
        const paralellTimeline = this.gsapAnimateStep(p);
        // console.log('----------', p.delay);
        timeline.add(paralellTimeline, p.delay || 0);
      }
    }

    return timeline;
  }

  _gsapAnimate(animation: TextAnimationStep) {
    // -- // -- //
    const { duration, zoom, translate } = animation;
  }

  targetEntity: any;

  /**
   * Creates a soft drop shadow that wraps a container's visual bounds.
   * Drawn via high-res Canvas2D -> PIXI.Texture to avoid pixelation.
   */
  createContainerDropShadow(container: Container, opts: DropShadowOpts = {}) {
    const {
      color = '#000000',
      alpha = 0.35,
      blur = 24,
      offset = { x: 0, y: 0 },
      padding = 16,
      radius = 12,
      quality = 2,
    } = opts;

    if (!parent) {
      throw new Error('Container must be on stage or provide opts.parent');
    }

    console.log('---------- canvasScale ---------', this.cs.fitScale);

    // Utility: hex -> rgba string // -- //
    const hexToRgb = (hex: string) => {
      const h = hex.replace('#', '');
      const bigint = parseInt(
        h.length === 3
          ? h
              .split('')
              .map(c => c + c)
              .join('')
          : h,
        16,
      );
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `${r},${g},${b}`;
    };

    console.log('createContainerDropShadow', offset);

    // Draw rounded-rect path
    const roundedRectPath = (
      ctx: CanvasRenderingContext2D,
      x: number,
      y: number,
      w: number,
      h: number,
      r: number,
    ) => {
      const rr = Math.min(r, w * 0.5, h * 0.5);
      console.log('roundedRectPath', { r, rr, x, y });

      ctx.beginPath();
      ctx.moveTo(x + rr, y);
      ctx.lineTo(x + w - rr, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
      ctx.lineTo(x + w, y + h - rr);
      ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
      ctx.lineTo(x + rr, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
      ctx.lineTo(x, y + rr);
      ctx.quadraticCurveTo(x, y, x + rr, y);
      ctx.closePath();
    };

    // Create shadow sprite from current bounds
    const makeTexture = () => {
      // Global bounds of the container in world space
      const b = container.getBounds();

      // Box we want the shadow for
      const boxW = Math.max(1, b.width + padding * 2);
      const boxH = Math.max(1, b.height + padding * 2);

      // Renderer resolution and oversampling
      const res = (this.cs.app.renderer as any).resolution ?? 1;
      const sample = Math.max(1, Math.floor(res * quality));

      // Canvas dimensions for blur spread
      const extra = blur * 2;
      const canvasW = Math.ceil((boxW + extra) * sample);
      const canvasH = Math.ceil((boxH + extra) * sample);

      // Semi-transparent background to see the canvas extent

      const canvas = document.createElement('canvas');
      canvas.width = canvasW;
      canvas.height = canvasH;

      const ctx = canvas.getContext('2d')!;
      ctx.scale(sample, sample);

      if (true) {
        ctx.save();
        ctx.globalAlpha = 0.12;
        ctx.fillStyle = '#00AAFF';
        ctx.fillRect(0, 0, canvasW + extra, canvasH + extra);
        ctx.restore();

        // Canvas border
        ctx.save();
        ctx.lineWidth = 1;
        ctx.strokeStyle = '#00AAFF';
        ctx.fillRect(0, 0, canvasW + extra, canvasH + extra);
        ctx.restore();

        ctx.save();
        ctx.filter = 'none';
        ctx.globalAlpha = 1;
        ctx.lineWidth = 1;
        ctx.strokeStyle = '#FF3366'; // red-ish
        roundedRectPath(ctx, blur, blur, boxW, boxH, radius);
        ctx.stroke();

        // Crosshair at the canvas center
        const cx = (boxW + extra) / 2;
        const cy = (boxH + extra) / 2;
        ctx.strokeStyle = '#22CC88';
        ctx.beginPath();
        ctx.moveTo(cx, 0);
        ctx.lineTo(cx, boxH + extra);
        ctx.moveTo(0, cy);
        ctx.lineTo(boxW + extra, cy);
        ctx.stroke();
        ctx.restore();
        // Blur/fill styles // -- //
      }

      ctx.filter = `blur(${blur}px)`;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = `rgba(${hexToRgb(color)},1)`;

      // Draw box inside canvas (leaving blur room) //
      const drawX = blur;
      const drawY = blur;
      const drawW = boxW;
      const drawH = boxH;

      // Apply offset
      const ox = offset.x;
      const oy = offset.y;

      roundedRectPath(ctx, drawX + ox, drawY + oy, drawW, drawH, radius);
      ctx.fill();

      // Convert canvas to PIXI texture + sprite
      const tex = Texture.from(canvas);
      tex.baseTexture.scaleMode = SCALE_MODES.LINEAR;

      const sprite = new Sprite(tex);
      sprite.anchor.set(0, 0);
      sprite.alpha = 1;

      // Position shadow inside container
      const topLeftWorld = { x: -padding - blur, y: -padding - blur };
      sprite.position.set(
        topLeftWorld.x - 2 * (blur + padding) - extra,
        topLeftWorld.y - (blur + padding),
      );

      return {
        box: { w: boxW, h: boxH },
        sample,
        blurPixels: blur,
        sprite,
      };
    };

    // Initial build
    const { sprite } = makeTexture();

    // Insert behind content inside container
    container.addChildAt(sprite, 0);

    return {};
  }

  makeHorizontalGradientMask(width: number, height: number, fadePx: number) {
    // Canvas to draw an opaque block + a right-edge gradient to transparent // -- //
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(width + fadePx));
    canvas.height = Math.max(1, Math.ceil(height));

    const ctx = canvas.getContext('2d')!;

    // 1) Solid white (fully visible) area
    ctx.fillStyle = 'rgba(255,255,255,1)';
    ctx.fillRect(0, 0, width, height);

    // 2) Right-edge gradient from white -> transparent
    const g = ctx.createLinearGradient(width, 0, width + fadePx, 0);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(width, 0, fadePx, height);

    return Texture.from(canvas);
  }

  parentWidth: number;
  parentHeight: number;

  reAlignInCell() {
    this.alignInCell(this.parentWidth, this.parentHeight);
  }

  alignInCell(parentWidth: number, parentHeight: number, noApply?: boolean) {
    // const { w, h, x, y, margin, align } = this.cellAlignment;

    this.parentWidth = parentWidth;
    this.parentHeight = parentHeight;

    const [w, h] = [this.baseWidth, this.baseHeight];

    const align = this.descriptor?.textConfig?.align || 'center';
    let newX: number;
    switch (align) {
      case 'left':
        newX = 0;
        break;
      case 'right':
        newX = Math.max(0, parentWidth - w);
        break;
      case 'justify':
        newX = 0; // occupy full line width; horizontal start at 0
        break;
      case 'center':
      default:
        newX = (parentWidth - w) / 2;
        break;
    }
    const newY = (parentHeight - h) / 2;

    // -- // -- //
    if (noApply) {
      return {
        x: newX,
        y: newY,
      };
    }

    this.translate.x = newX;
    if (!this.descriptor?.textConfig?.noYAlign) {
      this.translate.y = newY;
    }

    if (this.text == 'Just describe the content') {
      console.log('applyTranslate', this.translate);
    }
    this.applyTranslate(this.translate);
    return this.translate;
  }

  centerAlign() {
    // -- //
  }

  /* applyTranslate(translate: Translate, setTranslate?: boolean): void {
    super.applyTranslate(translate, setTranslate);
    console.log('ts.applyTranslate', translate);
  } */

  enablePointerEvents() {
    this.newLetters.map(letter => letter.enablePointerEvents());
  }
  disablePointerEvents() {
    this.newLetters.map(letter => letter.disablePointerEvents());
  }

  afterInit(): void {
    super.afterInit();

    // console.log('text-shape > afterInit');
    this.container.eventMode = 'passive';

    // -- //
    if (this.fontFamily) {
      if (this.parent instanceof RootShape) {
        this.parent.shapesToBeLoaded[this.resourceData.IRI] = true;
      }
      this.hide();
      this.service.fontService._loadFont(this.fontFamily).then(() => {
        // console.log('----font-load-ed -----', this.fontFamily);
        this.refreshElement();
        // if (this.descriptor.autoScale) {
        //   // -- //
        //   this.autoScale();
        // }

        // this.doAlign(this.descriptor.cellAlignment);
        if (this.descriptor.cellAlignment) {
          this.alignInCell(
            this.descriptor.cellAlignment.w,
            this.descriptor.cellAlignment.h,
          );
        }
        if (this.hiddenBySetPreAnimationState) {
          return;
        }

        this._show();
        if (this.parent instanceof RootShape) {
          delete this.parent.shapesToBeLoaded[this.resourceData.IRI];
        }
        if (this.config?.initial || this.config?.initialType === 'text') {
          setTimeout(() => {
            // this.selectWord(this.words[0]);
          }, 1);
        }
      });
    } else {
      // this._show();
      const translate = this.doAlign(this.descriptor.cellAlignment, {
        // noSetDescriptor: true,
      });
      // -- //
    }

    Object.values(this.animationsById || {}).map(animations => {
      if (animations['type-animation']) {
        this.hide();
        this.hiddenByAnimation = true;
      }
    });
  }

  /* _show() {
    console.log('ts._show');
    super._show();
  }

  _hide() {
    console.log('ts._hide');
    super._hide();
  } */

  saveKey(
    innerKey: keyof TextConfig,
    value: number | string | boolean,
    direct = false,
  ) {
    console.log('saveKey', { innerKey, value });
    this.store.dispatch(
      setDescriptorValue({
        IRI: this.IRI,
        key: 'textConfig',
        innerKey,
        value,
        direct,
      }),
    );
  }

  // updateOpacity(opacity: number) {
  //   this._show();
  // }

  _show() {
    super._show();
    this.lines.map(line => (line.container.alpha = 1));
    this.words.map(
      word => (word.container.alpha = this.textConfig.opacity || 1),
    );
  }

  envChanged(env: Record<string, any>) {
    super.envChanged(omit(env, this.keys));
    if (!env) {
      return;
    }
    let changed = false;
    this.keys.map(key => {
      if (env[key] && env[key] !== this[key]) {
        this[key] = env[key];
        changed = true;
      }
    });
    if (changed) {
      this.refreshElement();
      this.save();
    }
  }

  typeAnimation: {
    originalText: string;
    current: number;
    division: number;
  };
  start: number;
  prepareKeyValueAnimation(
    animation: AnimationItem,
    division: number,
    duration: number,
  ) {
    super.prepareKeyValueAnimation(animation, division, duration);
    // console.log('text-shape > prepareKeyValueAnimation', animation);
    const { key, value } = animation;
    switch (key as string) {
      case 'fontWeight':
        this.fontWeight = value as 'normal' | 'bold' | 'bolder' | 'lighter';
        this.refreshElement();
        break;
    }
  }

  prepareShowHideAnimation(
    { value: show, x, y, scaleX, scaleY }: ShowHideAnimation,
    division: number,
    duration?: number,
    meta?: IWAnimationMetaData,
  ): void {
    // console.log('--------- prepare-show-hide animation ---------------');
    this.startBaseAnimation(show ? 'appear' : 'disappear', {
      start: 0,
      end: division,
    });
  }

  async baseAnimate(type: 'appear' | 'disappear') {
    const {
      float,
      scale,
      by,
      type: animationType,
    } = this.descriptor.baseAnimations[type] as TextAnimationConfig;

    // -- // -- // -- //
    this.startBaseAnimation(type, {
      start: 0,
      end: 60,
    });

    await this.animationService.animate((increment: number) =>
      this.incrementBaseAnimation(increment),
    );
  }

  textShapeAnimationState: ShapeAnimationState<TextAnimationConfig>;

  startBaseAnimation(
    type: 'appear' | 'disappear',
    { start, end }: { start: number; end: number },
  ) {
    console.log('text-start-base-animation', { start, end });
    super.startBaseAnimation(type, { start, end });
    const division = end - start;
    this.textShapeAnimationState = {
      start,
      end,
      division,
      current: 0,
      state: 'in-progress',
      type,
    };

    // this._letters.map(letter => letter.setMask(this.maskRectangle));
    const overlap = 0.2;

    // if (type == 'appear') {
    //   const { appear } = this.descriptor.baseAnimations || {
    //     appear: {
    //       by: 'line',
    //       type: 'fade',
    //       motion: {
    //         scale: 'grow',
    //       },
    //     } as TextAnimationConfig,
    //   };
    //   console.log('descriptor.baseAnimations', this.descriptor.baseAnimations);
    //   this._show();

    //   if (!appear) {
    //     this.textShapeAnimationState = null;
    //     return;
    //   }

    //   this.textShapeAnimationState.config = appear || {
    //     type: 'fade',
    //   };
    //   const { by, type, scale, float } = appear as TextAnimationConfig;
    //   // -- // -- // -- //

    //   console.log('appear', appear);
    //   switch (by) {
    //     case 'line':
    //       // -- // -- // -- //
    //       this.animationService
    //         .getWaterfallByOverlap(
    //           division,
    //           this.lines.length,
    //           overlap * division,
    //         )
    //         .map((section, index) => {
    //           this.lines[index].startAnimation(section, appear);
    //         });

    //       break;
    //     case 'letter':
    //       // const {}

    //       const letterSections = this.animationService.getWaterfallByOverlap(
    //         division,
    //         this.letters.length,
    //         overlap * division,
    //       );

    //       console.log('letter-sections', letterSections);

    //       // -- // -- // -- //
    //       this.letters.map((letter, index) => {
    //         letter.startAnimation(letterSections[index]); // -- // -- //
    //       });
    //       break;
    //     // case 'word': // -- //
    //     default:
    //       const words = this.words.filter(word => word.text !== ' ');
    //       const wordSections = this.animationService.getWaterfallByOverlap(
    //         division,
    //         words.length,
    //         overlap * division,
    //       );
    //       words.map((word, index) => {
    //         word.startAnimation(
    //           wordSections[index],
    //           appear as TextAnimationConfig,
    //         );
    //       });
    //       break;
    //   }
    // } else {
    //   // this.startTextAnimation(disappear, division);
    // }
    // this._show();
  }

  incrementAnimationByKey(key: AnimationKeys, increment: number): void {
    super.incrementAnimationByKey(key, increment);
    // this.incrementBaseAnimation(increment);
  }
  incrementBaseAnimation(increment: number): boolean {
    super.incrementBaseAnimation(increment);
    // if (!this.textShapeAnimationState) {
    //   return true;
    // }
    return false;
    // this.textShapeAnimationState.current += increment;
    // const { current, start, end, state, type, config } =
    //   this.textShapeAnimationState;
    // if (state == 'finished') {
    //   return true;
    // }
    // if (current < start) {
    //   return;
    // }
    // if (end < current && state == 'in-progress') {
    //   this.endBaseAnimation();
    //   return true;
    // }
    // if (type == 'appear') {
    //   const { type, by } = config as TextAnimationConfig;
    //   switch (by) {
    //     case 'line':
    //       this.lines.map(line => {
    //         line.incrementAnimation(increment);
    //       });
    //       break;
    //     case 'letter':
    //       this.letters.map(letter => {
    //         letter.incrementAnimation(increment);
    //       });
    //       break;
    //     case 'word':
    //     default:
    //       this.words
    //         .filter(word => word.text !== ' ')
    //         .map((word, index) => {
    //           word.incrementAnimation(increment, index);
    //         });
    //       break;
    //   }
    // }
  }

  endBaseAnimation() {
    super.endBaseAnimation();
    // if (this.textShapeAnimationState.state == 'finished') {
    //   return;
    // }

    // this.textShapeAnimationState.state = 'finished';

    // const { appear } = {
    //   appear: {
    //     type: 'fade',
    //     by: 'word',
    //   },
    // };

    // if (this.textShapeAnimationState.type == 'appear') {
    //   const { by } = appear;

    //   switch (by) {
    //     case 'letter':
    //       this.letters.map(letter => {
    //         letter.removeMask();
    //         letter.endAnimation();
    //       });
    //       break;
    //     case 'word':
    //       this.words
    //         .filter(word => word.text !== ' ')
    //         .map(word => {
    //           word.endAnimation();
    //         });
    //       break;
    //   }
    // }
  }
  startAnimation(
    id: string,
    division: number,
    inverse?: boolean,
    duration?: number,
  ): void {
    this.startTextAnimation(
      {
        letterAnimation: {
          type: 'float',
        },
      },
      division,
      inverse,
      duration,
    );
    return;
    Object.values(this.animationsById?.[id] || {}).map(({ key, value }) => {
      switch (key) {
        case 'text':
          this.text = value as string;
          this.refreshElement();
          break;
        case 'fontSize':
          this.fontSize = value as number;
          this.refreshElement();
          break;
        case 'fontWeight':
          this.fontWeight = value as 'normal' | 'bold' | 'bolder' | 'lighter';
          this.refreshElement();
          break;
        case 'type-animation':
          this._show();
          this.start = performance.now();
          this.typeAnimation = {
            originalText: `${this.textValue}`,
            current: 0,
            division: division / this.textValue.length,
          };
          this.text = '';
          this.refreshElement();
          break;
        case 'text-animation':
          const { letterAnimation, wordAnimation } =
            value as TextAnimationValue;
          // -- // -- //

          console.log('--------- x --------');

          break;
      }
    });
    // this.animationsById?.[id]; //
    //super.startAnimation(id, division, inverse, duration);
  }
  wordAnimationIncrement = 0;
  letterAnimationIncrement = 0;
  startTextAnimation(
    value: TextAnimationValue,
    division: number,
    inverse?: boolean,
    duration?: number,
  ) {
    // const n = this._letters.filter(char => char !== ' ').length;
    // const d = division / (1 / 3 + (2 * n) / 3);
    // this._letters.map((letter, index) => {
    //   letter.startAnimation(d, index);
    // });
  }

  incrementAnimation(
    increment: number,
    id?: string,
    maxIncrement?: number,
  ): void {
    super.incrementAnimation(increment);
    this.incrementBaseAnimation(increment);
    // -- //
    // this.letters.map((letter, index) => {
    //   letter.incrementAnimation(increment);
    // });
    // if (this.typeAnimation) {
    //   this.typeAnimation.current += increment;
    //   const { current, division, originalText } = this.typeAnimation;
    //   const maxChars = Math.floor(current / division);
    //   this.text = originalText.slice(0, maxChars);
    //   this.refreshElement();
    // }

    // if (this.fillColorIncrement) {
    //   this.words.map(word =>
    //     word.textElement.patch({
    //       fill: this.fillColorIncrement.increment(increment),
    //     }),
    //   );
    // }
  }

  endAnimationByKeyValue(key: string, value: any): void {
    // this.letters.map(letter => {
    //   letter.endAnimation();
    // });
    // super.endAnimationByKeyValue(key, value);
    // if (this.typeAnimation) {
    //   this.typeAnimation = null;
    //   this.text = this.descriptor.text;
    //   this.refreshElement();
    // }
  }

  unMovedRCDiagonal: number;
  unMovedFontSize: number;

  unMovedConfig: TextConfig;
  unMovedWordWrapWidth: number;
  unMovedDim: Coords;
  cursor: PathElement;
  currentText = '';
  selectedEditMode = false;
  baseLineHeight: number;
  init() {
    super.init();

    this.setBaseLineHeight();

    // this.textElement = new TextElement(this, this.container, {
    //   text: this.textValue,
    //   fontSize: this.fontSize,
    // });

    this.refreshElement();

    const [width, height] = this.dim;
    this.maskRectangle = new RectangleElement(this, this.container, {
      width,
      height,
      x: this.x,
      y: this.y,
    });
    // this.textElement
    //   .click(() => {
    //     console.log('te > clicked');
    //     // -- // -- //
    //     this.clicked();
    //   })
    //   .doubleclick(coords => {
    //     console.log('double-click', coords);
    //     const [xa, ya] = coords;
    //     console.log('x', xa - this.x, 'y', ya - this.y);
    //     const [x, y] = [xa - this.x, ya - this.y];

    //     if (!this.selected) {
    //       this.select();
    //     }
    //     // this.wordWrap = false;
    //     // this.wordWrapWidth = Infinity; //
    //     this.selectedEditMode = true;
    //   })
    //   .mouseover(() => {
    //     this.select({ onHover: true });
    //   })
    //   .mouseout(() => {
    //     this.deselect({ onHover: true });
    //   })
    //   .drag(
    //     (dx, dy) => {
    //       this.service.drag(dx, dy);
    //     },
    //     () => this.localStartDrag(),
    //     () => {
    //       // console.log('ps.element > endDrag'); //
    //       this.service.endDrag();
    //     },
    //   );

    // -- //

    // if (this.config?.isRoot) {
    // console.log('init-heyhey', this.x, this.y); // -- // -- // -- // -- //

    this.rc = new RectangleController(
      this,
      {
        width,
        height,
        showRectangle: true,
        noFill: true,
        // symmetric: false,
        // offset: [this.x, this.y],
        scaleMode: true,
        centerBased: true,
        startDrag: p => this.rcStartDrag(p),
        drag: resp => this.rcDrag(resp),
        endDrag: point => this.rcEndDrag(point),
        clicked: () => this.clicked(),
        mouseout: () => {
          // if (!this.cs.shapeAddMode) {
          //   this.deselect({ onHover: true });
          // }
        },
        skipRC: {
          center: true,
        },
      },
      this.circleContainer,
      this.auxCircleContainer,
      this.container,
    );

    this.rc.hide();
    // } //

    this.selectedKeyEventSubscribe('f+ArrowUp', () => {
      this.fontSize++;
      this.refreshElement();
    });
    this.selectedKeyEventSubscribe('f+ArrowDown', () => {
      this.fontSize--;
      this.refreshElement();
    });
    this.selectedKeyEventSubscribe('f+ArrowRight', () => {
      this.fontSize += 5;
      this.refreshElement();
      this.cs.consumeKeyEvent('ArrowRight');
    });
    this.selectedKeyEventSubscribe('f+ArrowLeft', () => {
      this.fontSize -= 5;
      this.refreshElement();
      this.cs.consumeKeyEvent('ArrowLeft');
    });

    this.keyEventSubscribe;

    /* console.log(
      'text-shape > init',
      this.dim,
      'parent.dim',
      this.rootParent.dim,
    ); */
  }

  setBaseLineHeight() {
    this.baseLineHeight = this.measureText(
      'abc',
      new TextStyle({
        ...this.elementStyle,
        lineHeight: undefined,
      }),
    ).lineHeight;
  }

  numberOfLines: number;
  minLineHeight: number;
  maxXCoord: number;
  maxYCoord: number;

  rcStartDrag(point?: RCPoint) {
    // -- // -- //
    this.unMovedFontSize = this.fontSize;
    this.unMovedWordWrapWidth = this.wordWrapWidth;
    this.unMovedDim = this.dim as Coords;
    this.unMovedConfig = this.textConfig;
    this.numberOfLines = this.lines.length;
    // -- // -- //
    // console.log('rcStartDrag', this.fontSize);
    // console.log('rcStartDrag.unMovedDim', this.unMovedDim);
    // console.log('rcStartDrag.wordWrapWidth', this.wordWrapWidth);

    this.minLineHeight = this.measureText(
      'abc',
      new TextStyle({
        ...this.elementStyle,
        lineHeight: undefined,
      }),
    ).lineHeight;

    if (point == 'left-center') {
      // in that case we need to restrict x coords
      const currentWidth = this.wordWrap
        ? this.wordWrapWidth
        : this.unMovedDim[0];

      const maxWidthShrink = currentWidth - this.maxWordWidth;
      this.maxXCoord = this.x + maxWidthShrink;
    }

    if (point == 'center-top') {
      // in that case we need to restrict x coords
      const currentHeight = this.unMovedDim[1];

      // -- // -- // -- // -- //
      const minHeight =
        this.baseLineHeight +
        this.baseLineHeight * (this.numberOfLines - 1) * 0.5;
      const maxHeightShrink = currentHeight - minHeight;

      this.maxYCoord = this.y + maxHeightShrink;
    }
  }

  rcDrag({ x, y, dx, dy, width, height, point, scale }: RectangleDragResponse) {
    // -- // -- //
    switch (point) {
      case 'center-top':
      case 'center-bottom':
        // const lineHeight = Math.max(
        //   this.baseLineHeight / 2,
        //   height / this.numberOfLines,
        // );

        if (height / this.numberOfLines < this.baseLineHeight) {
          height = Math.max(
            height,
            this.baseLineHeight +
              this.baseLineHeight * (this.numberOfLines - 1) * 0.5,
          );

          this.rc?.updateRectangle({ height });

          this.lineHeight =
            (height - this.baseLineHeight) /
            (this.baseLineHeight * (this.numberOfLines - 1));
        } else {
          this.lineHeight = height / (this.baseLineHeight * this.numberOfLines);
        }
        this.lineHeight = +this.lineHeight.toFixed(1);
        // console.log('line-height', this.lineHeight); // -- //
        this.saveKey('lineHeight', this.lineHeight);

        this.refreshElement(true);

        break;
      case 'left-center':
      case 'right-center':
        this.wordWrap = true;
        this.wordWrapWidth = Math.max(this.maxWordWidth, width);

        // -- //
        //  As the number of lines can change upon the wordWrapWidth the height of the RC might change
        // -- //

        this.refreshElement(true);
        const [, h] = this.dim;
        // if (width < this.maxWordWidth) {

        // }
        this.rc.updateRectangle({
          height: h,
          width: Math.max(width, this.maxWordWidth),
        });

        // -- // -- // -- //

        break;
      default:
        this.fontSize = Math.floor(scale * this.unMovedFontSize);
        if (this.wordWrap) {
          this.wordWrapWidth = Math.max(
            this.maxWordWidth,
            scale * this.unMovedWordWrapWidth,
          );
        }

        // -- // -- //
        this.refreshElement(true);
        break;
    }

    this.dx = dx;
    this.dy = dy;

    /* console.log('apply-translate', {
      x: point == 'left-center' ? Math.min(x, this.maxXCoord) : x,
      y: point == 'center-top' ? Math.min(y, this.maxYCoord) : y,
    }); */
    // -- // -- // -- //
    this.applyTranslate({
      x: point == 'left-center' ? Math.min(x, this.maxXCoord) : x,
      y: point == 'center-top' ? Math.min(y, this.maxYCoord) : y,
    });

    if (
      this.parent instanceof ContainerShape ||
      this.parent instanceof RootShape
    ) {
      this.alignInCell(this.maxLineWidth, this.baseLineHeight);
    }
  }
  rcEndDrag(point: RCPoint) {
    switch (point) {
      case 'center-top':
      case 'center-bottom':
        if (this.lineHeight < 1) {
          this.rc.patch(
            {
              height:
                this.lineHeight *
                  (this.numberOfLines - 1) *
                  this.minLineHeight +
                this.minLineHeight,
            },
            true,
          );
        } else {
          this.rc.patch(
            {
              height: this.lineHeight * this.numberOfLines * this.minLineHeight,
            },
            true,
          );
        }

        if (this.lineHeight == this.maxLineWidth) {
          this.lineHeight = undefined;
        }

        this.saveKey('lineHeight', this.lineHeight);
        break;
      case 'left-center':
      case 'right-center':
        const [width, height] = this.dim;
        this.rc.patch({ height }, true);
        break;
      default:
        this.saveKey('fontSize', this.fontSize);
        break;
    }

    this.saveKey('wordWrap', this.wordWrap);
    this.saveKey('wordWrapWidth', this.wordWrapWidth);
    this.saveTranslate(this.dx, this.dy);
    this.setBaseLineHeight();
  }
  select(params?: ShapeSelectParams) {
    const { onHover } = params || {};

    if (onHover) {
      // this comes from the text-letter element
      if (!this.selected) {
        this.rc.show({
          withoutPCs: true,
        });
      }
      return;
    }

    super.select(params);
    this.newLetters.map(letter => letter.showRectangles());
    this.rc?.show();
    this.selectWholeText();
  }
  selectEachword() {
    // -- // -- // -- //
  }

  initRC() {
    return;
    this.rc?.hide();
    // -- // -- //
    const [width, height] = this.dim;
    // -- // -- //
    this.rc = new RectangleController(
      this,
      {
        width,
        height,
        showRectangle: true,
        // noFill: true, //
        offset: [this.x, this.y],
        scaleMode: true,
        centerBased: true,
        startDrag: p => this.rcStartDrag(p),
        drag: resp => this.rcDrag(resp),
        endDrag: point => this.rcEndDrag(point),
        clicked: () => this.clicked(),
        mouseout: () => {
          // -- // -- //
          if (!this.cs.shapeAddMode) {
            this.deselect({ onHover: true });
          }
        },
        skipRC: {
          center: true,
        },
      },
      this.circleContainer,
      this.auxCircleContainer,
      this.container,
    );
  }

  updateSelection() {
    // -- // -- // -- // -- // -- //
    const [start, end] = [
      this.currentSelectionIndex,
      this.selectionStartIndex,
    ].sort((a, b) => a - b);

    if (start == end) {
      this.newLetters.map(letter => letter.clearSelection());
      return;
    }

    this.newLetters.map(letter => letter.updateSelection(start, end));
  }

  changeTookPlace = false;
  keyDownHandler(key: string) {
    return;
    /* console.log('keyDownHandler', {
      currentSelection: this.currentSelectionIndex,
      selectionStart: this.selectionStartIndex,
    }); */
    // console.log('---------------------currentLetter', !!this.currentLetter);
    if (key.startsWith('Arrow') && this.currentSelectionIndex == null) {
      this.selectionStartIndex = 0;
      this.currentSelectionIndex = 0;
      this.currentLetter?.showCursor();
      this.updateSelection();
      return;
    }

    switch (key) {
      case 'ArrowUp':
        this.currentLetter?.arrowUp();
        return;
      case 'ArrowDown':
        this.currentLetter?.arrowDown();
        return;
      case 'ArrowLeft':
        console.log('ArrowLeft', !!this.currentLetter);
        this.currentLetter?.arrowLeft();
        return;
      case 'ArrowRight':
        this.currentLetter?.arrowRight();
        return;
      // case 'Enter':
      //   key = '\n';
      //   if (this.cursorWord.lastCursorPositionInLine) {
      //     console.log('----- e-e -------');
      //     key = '\n\n';
      //     this.selectionStartIndex++;
      //     this.selectionEndIndex++;
      //   }
      //   break;
      case 'Space':
      case ' ':
        this.typing(' ');
        break;
      case 'Meta':
      case 'Shift':
        break;
      // case 'Backspace':
      //   key = '';
      //   // -- // -- // -- //
      //   this.selectionStartIndex--;
      //   // increase = 0;
      //   break;
      default:
        this.typing(key);
        break;
    }

    // console.log('clearSelection');
    this.clearSelection();
  }

  clearSelection() {
    this.newLetters.map(letter => letter.clearSelection());
  }
  typing(key: string) {
    // if (this.curren == null) {
    //   // console.log('selectionStartIndex is null'); // -- //
    //   return;
    // }

    const backspace = key == 'Backspace';
    const space = key == 'Space';
    const enter = key == 'Enter';

    // -- // -- //
    if (enter) {
      key = '\n';
    }

    if (space) {
      key = ' ';
    }

    if (backspace) {
      key = '';
    }

    let start: number, end: number;

    if (this.multipleLettersSelected) {
      start = Math.min(this.selectionStartIndex, this.currentSelectionIndex);
      end = Math.max(this.selectionStartIndex, this.currentSelectionIndex);
    } else {
      start = this.currentSelectionIndex;
      end = this.currentSelectionIndex;
    }

    // -- // console.log('typing', { start, end, key });

    if (backspace && start == end) {
      start--;
    }

    const preTextValue = this.text.slice(0, start);
    const postTextValue = this.text.slice(end);

    this.text = preTextValue + key + postTextValue;
    // console.log('text', this.text);

    console.log({
      start,
      end,
      multi:
        this.selectionStartIndex != null &&
        this.selectionStartIndex !== this.currentSelectionIndex,
    });

    // this.doAlign(this.cellAlignment);

    if (this.multipleLettersSelected) {
      console.log('------- 1.if ---------', {
        selectionStartIndex: this.selectionStartIndex,
        currentSelectionIndex: this.currentSelectionIndex,
      });

      if (backspace) {
        this.currentSelectionIndex = start;
      } else {
        // -- //
        this.currentSelectionIndex = start + 1;
      }
    } else {
      // console.log('------- 1.else ---------');
      if (backspace) {
        // this.currentLetter?.arrowLeft();
        this.currentSelectionIndex--;
      } else {
        this.currentSelectionIndex++;
        // this.currentLetter?.arrowRight();
      }
    }
    // if (this.multipleLettersSelected) //

    this.refreshElement();
    this.newLetters.map(letter => letter.showRectangles());
    // this.refreshElement();
    this.saveKey('text', this.text);
    // this.saveKey('fontSize', this.fontSize);
    this.currentLetter?.showCursor();
    this.selectionStartIndex = this.currentSelectionIndex;
    this.finishUpdate();
  }

  hideDragControllers(): void {
    super.hideDragControllers();
    // this.lines.map(line => line.hideRectangle());
    this.cursor?.hide();

    // this.saveKey('text', this.currentText);
    // if (this.changeTookPlace) {
    //   this.finishUpdate();
    // }

    //  -- // -- //
    // this.selectedEditMode = false;
    // -- // -- //

    this.cursor?.hide();
    // this.cs.deregisterKeyDownHandler();
  }

  regenerateAnimation() {
    // const { newAnimation } = this.cs.currentRootShape.descriptor;
  }

  deselect(params?: ShapeSelectParams): void {
    super.deselect(params);
    // this.lines.map(line => line.hide());
    this.newLetters.map(letter => letter.hideRectangles());
    this.newLetters.map(letter => letter.hideCursor());
    this.rc?.hide();
  }

  startTransformation(dx?: number, dy?: number): void {
    super.startTransformation(dx, dy);
    this.unMovedDim = this.dim;
    this.unMovedRCDiagonal = Math.sqrt(
      Math.pow(this.unMovedDim[0], 2) + Math.pow(this.unMovedDim[1], 2),
    );
    console.log('ts - startWordWrapWidth', this.wordWrapWidth);

    this.rcStartDrag();
  }

  _scaleX: number;
  _scaleY: number;
  transformRatio: number;
  transformation(scaleX: number, scaleY: number, dx?: number, dy?: number) {
    const [x, y] = this.unMovedDim;
    const length = Math.sqrt(Math.pow(x * scaleX, 2) + Math.pow(y * scaleY, 2));

    this.transformRatio = length / this.unMovedRCDiagonal;

    // (this.fontSize = this.transformRatio * this.fontSize),
    // this.textElement.patch({
    //   fontSize: this.transformRatio * this.fontSize,
    //   wordWrap: this.wordWrap,
    //   wordWrapWidth: this.wordWrap
    //     ? Math.floor(this.transformRatio * this.wordWrapWidth)
    //     : undefined,
    // });

    this.fontSize = Math.floor(this.transformRatio * this.unMovedFontSize);
    if (this.wordWrap) {
      this.wordWrapWidth = Math.max(
        this.maxWordWidth,
        this.transformRatio * this.unMovedWordWrapWidth,
      );
    }

    const [width, height] = this.dim;

    this.rc.updateRectangle({
      width,
      height: height * this.transformRatio,
      // height: h,
      // width: Math.max(width, this.maxWordWidth),
    });

    // -- // -- //
    this.refreshElement(true);
    super.transformation(scaleX, scaleY, dx, dy, true);
  }
  endTransformation(): void {
    super.endTransformation();
    this.fontSize *= this.transformRatio;
    this.saveKey('fontSize', this.fontSize);
    if (this.wordWrap) {
      this.wordWrapWidth *= this.transformRatio;
      this.saveKey('wordWrapWidth', this.wordWrapWidth);
    }
  }
  convertHexToNumber(value: string) {
    return value ? Number(`0x${value.substring(1)}`) : 0;
  }

  selectWholeText() {
    this.selectionStartIndex = 0;
    this.currentSelectionIndex = this.newLetters.length - 1;
    this.updateSelection();
  }

  refresh() {
    super.refresh();
    this.refreshElement();
  }

  // updateText() {
  //   if (this.cs.currentAnimation) {
  //     // -- // -- //
  //   } else {
  //     this._updateText();
  //   }
  // }

  // positions: Array<
  //   {
  //     char: string;
  //     x: number;
  //     y: number;
  //   }[]
  // >;

  /**
  It is set by the text-words
  */
  maxWordWidth = 0;
  maxLineWidth = 0;
  _style: TextStyle;

  get elementStyle() {
    const style = {
      fontSize: this.fontSize,
      fontStyle: this.fontStyle,
      // fill: this.color?.startsWith?.('$')
      //   ? this.service.resolveColor(color)
      //   : color,
      wordWrap: this.wordWrap,
      wordWrapWidth: this.wordWrapWidth,
      letterSpacing: this.letterSpacing,
      // fill: this.textConfig.color,
      // stroke: stroke ? this.service.resolveColor(stroke.color) : undefined,
      // strokeThickness: stroke?.width, //
      fontFamily: this.fontFamily || 'Arial',
      lineHeight: this.lineHeight,
      // trim: true, // -- //
      fontWeight: this.fontWeight,

      // opacity, // -- //
      align: this.align,
    } as ITextStyle;

    const { fill: fillValue, stroke, opacity } = this.svgAttributes;

    const { color } = fillValue || {};

    const fill = color?.startsWith?.('$')
      ? this.service.resolveColor(color)
      : color;
    if (fill) {
      style.fill = fill;
    }

    style.fill = this.textConfig.color || style.fill;

    // style.fill = ['#ff0000', '#0000ff'];
    // style.fillGradientType = TEXT_GRADIENT.LINEAR_HORIZONTAL;
    // style.fillGradientStops = [0, 1];
    if (this.textConfig.color) {
      // console.log('textConfig', this.textConfig);
    }

    return style;
  }

  characterWidthMap: Record<string, number> = {};
  lines: TextLine[];
  letterIndex: number;
  currentlySelecedLetter: TextLetter;
  letterClicked(letter: TextLetter) {
    this.currentlySelecedLetter = letter;
    // -- //
    this.newLetters.map(letter => letter.hideCursor());
  }

  getLetterIndex() {
    return this.letterIndex++;
  }

  _initLines(
    lines: {
      words: { text: string; width: number }[];
      width: number;
      wrapped: boolean;
    }[],
  ) {
    // -- // -- // -- // -- // -- //
    this.letterIndex = 0;

    const style = new TextStyle(this.elementStyle);
    const metric = this.measureText('Abc', style);
    // -- //

    const maxLineWidth = Math.max(...lines.map(line => line.width));

    this.lines = lines.map((line, index) => {
      return new TextLine(this, line, {
        maxLineWidth: this.wordWrap ? this.wordWrapWidth : maxLineWidth,
        lineHeight: metric.lineHeight,
        actualLineHeight: metric.lineHeight,
        letterIndexOffset: 0,
        style,
        index,
        width: line.width,
      });
    });

    // console.log('lines', lines); //
    return 1;
  }

  mouseEventHandler(
    type: 'arrow-left' | 'arrow-right' | 'arrow-up' | 'arrow-down',
  ) {
    // -- // -- // -- // -- // -- //

    if (this.cs.isShiftPressed) {
      // -- //
    } else {
      // -- //
    }
  }

  getDimensions() {
    let initialLines = this.text.split('\n');
    // console.log('initialLines', initialLines);

    // console.log('initialLines > before', initialLines);
    initialLines = initialLines
      .map(line => this.splitToAtMostWords(line, 50))
      .flat();

    let maxLineWidth = 0;
    let mmaxLineHeight = 0;
    let text = '';
    for (const line of initialLines) {
      const metric = this.measureTextMetric(line, this.elementStyle);

      if (maxLineWidth < metric.maxLineWidth) {
        text = line;
      }

      maxLineWidth = Math.max(maxLineWidth, metric.maxLineWidth);
      mmaxLineHeight = Math.max(mmaxLineHeight, metric.lineHeight);
    }

    return {
      width: maxLineWidth,
      height: mmaxLineHeight * initialLines.length,
      widestLine: text,
    };
  }

  splitToAtMostWords(line: string, max: number): string[] {
    // -- // -- //
    const words = line.split(' ');

    // -- //
    const numberOfChars = words.reduce((sum, word) => sum + word.length, 0);

    if (numberOfChars > max) {
      // get the words till 50
      let index = 0;
      const currentWords = [];
      let currentWidth = 0;
      for (const word of words) {
        // -- //
        currentWidth += word.length + 1;
        if (currentWidth > max) {
          // return [
          //   currentWords.join(' '),
          //   ...splitToAtMostWords(words.slice(index).join(' '), max),
          // ];
          break;
        }
        currentWords.push(word);
        index++;
      }

      return [
        currentWords.join(' '),
        ...this.splitToAtMostWords(words.slice(index).join(' '), max),
      ];
    }

    return [line];
  }

  getLineOptions: {
    splitByWords?: string[];
    newLineconsider?: boolean;
    replaceNewlineWithSpace?: boolean;
  };

  getLines(text: string) {
    // -- // -- // -- //
    const { replaceNewlineWithSpace } = this.getLineOptions || {};
    if (replaceNewlineWithSpace) {
      text = text.replace(/\n/g, ' ');
    }

    this.measureCharacters(text);
    // -- // console.log('text', text); // -- //
    let initialLines = text.split('\n');
    // console.log('initialLines', initialLines);

    // console.log('initialLines > before', initialLines); //
    initialLines = initialLines
      .map(line => this.splitToAtMostWords(line, 50))
      .flat();

    // console.log('initialLines > after', initialLines);
    const lines: Array<{
      words: Array<{ text: string; width: 0 }>;
      width: number;
      wrapped?: boolean;
    }> = [];

    for (const line of initialLines) {
      if (line == '') {
        lines.push({
          words: [{ text: '\n', width: 0 }],
          width: 0,
          wrapped: false,
        });
        continue;
      }
      const words = this._getWordsOfLine(line);
      // console.log({ words }); // -- //

      const wrappedLines = this.wrapLine(words, this.wordWrapWidth);
      // console.log('wrappedLines', wrappedLines);
      for (const wrappedLine of wrappedLines) {
        // console.log('wrappedLine', wrappedLine); //
        lines.push(wrappedLine);
      }
    }
    return lines as any;
  }

  measureCharacters(text: string) {
    this.characterWidthMap = {};
    const style = new TextStyle(this.elementStyle);
    for (const char of text) {
      if (this.characterWidthMap[char]) {
        continue;
      }
      if (char == ' ') {
        this.characterWidthMap[char] = this.spaceWidth;
        continue;
      }
      const metric = this.measureText(char, style);
      this.characterWidthMap[char] = metric.maxLineWidth;
      // -- // -- //
      // this.baseHeight = Math.max(this.baseHeight, metric.lineHeight);
    }
  }

  _getWordsOfLine(line: string) {
    //
    // return line.split(' ');
    const words: string[] = [];
    let currentWord = '';
    // -- // -- //
    for (const char of line) {
      if (char == ' ') {
        if (currentWord) {
          words.push(currentWord);
        }
        words.push(' ');
        currentWord = '';
      } else {
        currentWord += char;
      }
    }
    if (currentWord) {
      words.push(currentWord);
    }
    // -- // -- //
    return words.map(value => ({
      text: value,
      width: Array.from(value).reduce(
        (sum, char) => sum + this.characterWidthMap[char],
        0,
      ),
    }));
  }

  wrapLine(
    inputWords: { text: string; width: number }[],
    lineLength = Infinity,
  ) {
    // -- // -- //
    const lines = [];
    let words: { text: string; width: number }[] = [];
    let currentWidth = 0;

    let lastWord: string;
    let lastWidth: number;

    for (const word of inputWords) {
      const { text, width } = word;
      // -- // -- //

      if (lineLength <= currentWidth + width) {
        if (lastWord == ' ') {
          words.pop();
          currentWidth -= lastWidth;
        }
        words.push({ text: '\n', width: 0 });
        lines.push({ words: words, wrapped: true, width: currentWidth });
        if (text == ' ') {
          words = [];
          currentWidth = 0;
        } else {
          words = [word];
          currentWidth = width;
        }
      } else {
        words.push(word);
        currentWidth += width;
      }

      lastWord = text;
      lastWidth = width;
    }

    if (words.length) {
      words.push({ text: '\n', width: 0 });
      lines.push({ words: words, width: currentWidth });
    }

    return lines;
  }

  measureText(char: string, style: TextStyle) {
    return TextMetrics.measureText(char, style);
  }

  measureTextMetric(char: string, style: ITextStyle) {
    // -- // -- //
    const textStyle = new TextStyle({
      ...style,
      lineHeight: undefined,
    });
    return TextMetrics.measureText(char, textStyle);
  }
  spaceWidth: number;
  netLineHeight: number;

  currentSegment: [number, number];

  get segmentToOne() {
    return this.baseWidth / this.currentWidth;
  }

  previousWidth: number;
  nextWidth: number;

  get currentWidth() {
    // -- //
    if (this.currentSegment) {
      const [start, end] = this.currentSegment;
      return this.lines[0].words
        .slice(start, end)
        .reduce((acc, word) => acc + word.width, 0);
    }
    return this.baseWidth;
  }

  refreshElement(noRectUpdate = false) {
    // if (!this.textElement) {
    //   return;
    // }

    // console.log('------textShape.refreshElement', this.fontSize);
    const style = this.elementStyle;

    // if (this.lineHeight) {
    //   const lineHeight = this.measureText(
    //     'abc',
    //     new TextStyle({
    //       ...this.elementStyle,
    //       lineHeight: undefined,
    //     }),
    //   ).lineHeight;
    //   console.log('lineHeight', lineHeight, 'elementStyle', this.elementStyle);
    //   // style.lineHeight = lineHeight * this.lineHeight;
    // }

    style.lineHeight = undefined;
    this._style = new TextStyle(style);

    const offset = 0;

    const metric = this.measureText(this.textValue, this._style);

    // old
    // const spaceMetric = this.measureText('a', this._style);

    if (this.elementStyle.fill == '#000000') {
      console.log('-------------- hey hey hey -------------', this.text);
    }

    // new // -- //
    const outerMetic = this.measureText('a', this._style);
    const spaceMetric = this.measureText('a a', this._style);

    // -- //

    this.spaceWidth = spaceMetric.maxLineWidth - 2 * outerMetic.maxLineWidth;

    // this.baseHeight =
    this.maxWordWidth = 0;
    this.maxLineWidth = 0;
    this.lines?.map(line => line.remove());

    this.baseLineHeight = metric.lineHeight;

    this.netLineHeight = metric.lineHeight;

    const _lines = this.getLines(this.textValue);
    // console.log('characterWidthMap', this.characterWidthMap); // -- //
    this.maxLineWidth = Math.max(..._lines.map(line => line.width));

    this.baseWidth = this.maxLineWidth;
    // this.baseHeight = Math.max(..._lines.map(line => line.height));
    // console.log('maxLineWidth', this.maxLineWidth);
    this._initLines(_lines);

    // console.log('characterWidthMap', this.characterWidthMap); // -- //
    this.words.map(word => word.afterInit());

    // const { width, height } = this.getBounds();
    // this.baseWidth = width;
    // this.baseHeight = height;

    if (this.text == 'Meet') {
      // console.log('---------------bounds', width, height);
    }
    // this.lines = lines.map((line, index) => {
    //   const { words } = line;
    //   this.maxWordWidth = Math.max(
    //     this.maxWordWidth,
    //     Math.max(...words.map(({ width }) => width)),
    //   );

    //   const textLine = new TextLine(this, line, {
    //     maxLineWidth: this.wordWrap ? this.wordWrapWidth : metric.maxLineWidth,
    //     // lineHeight: this.lineHeight ? this.lineHeight : metric.lineHeight,
    //     lineHeight: metric.lineHeight,
    //     actualLineHeight: metric.lineHeight,
    //     letterIndexOffset,
    //     style,
    //     index,
    //     width: line.width,
    //   });

    //   // -- // -- // -- //
    //   const charlength =
    //     words.reduce((sum, word) => {
    //       sum += word.value.length;
    //       return sum;
    //     }, 0) || 1;
    //   letterIndexOffset += charlength + 1;

    //   return textLine;
    // });

    // this.words.map(word => word.afterInit());

    // this.numberOfLines = this.lines.length;
    this.numberOfLines = this.lines.length;

    // this.showSelectionRectangle();

    if (noRectUpdate) {
      return;
    }

    this.updateRect();
  }

  baseWidth: number;
  baseHeight: number;

  get absWidth() {
    return this.baseWidth / this.cs.fitScale;
  }

  get absHeight() {
    return this.baseHeight / this.cs.fitScale;
  }

  updateText(text?: string) {
    this.text = text || this.text;
    this.refreshElement();
  }

  autoScale() {
    this.opacity = 0.01;
    let ratio: number;
    switch (this.words.length) {
      case 1:
        ratio = 0.4;
        break;
      case 2:
        ratio = 0.6;
        break;
      case 3:
        ratio = 0.7;
        break;
      default:
        ratio = 0.8;
        break;
    }

    const refWidth = ratio * _screenX;

    // console.log('max-line-width', this.maxLineWidth);
    if (this.maxLineWidth < refWidth) {
      while (this.maxLineWidth < refWidth) {
        // -- // -- // -- // -- //
        this.fontSize++;
        // console.log('fontSize', this.fontSize); //
        this.refreshElement();
      }
    }

    // console.log('max-line-height', this.netLineHeight); //

    this.x = (_screenX - this.maxLineWidth) / 2;
    this.y = (_screenY - this.netLineHeight) / 2;

    this.applyTranslate({
      x: this.x,
      y: this.y,
    });
    this.opacity = 1;
  }

  /**************************************************************************
   * ALIGN / SCALE
   *************************************************************************/

  scaleToWidth(width: number) {
    // -- //
    console.log('---- scale-to-width ------', {
      maxLineWidth: this.maxLineWidth,
      width,
    });
    if (this.maxLineWidth == width) {
      return;
    }

    if (this.maxLineWidth < width) {
      while (this.maxLineWidth < width && this.fontSize < 10_000) {
        // -- // -- // -- // -- //
        this.fontSize++;
        this.refreshElement();
      }
    } else {
      while (this.maxLineWidth > width && 0 < this.fontSize) {
        // -- // -- // -- // -- //
        this.fontSize--;
        this.refreshElement();
      }
    }
  }

  alignHorizontally(dims: Coords, align?: 'left' | 'center' | 'right'): number {
    const [, h] = dims;
    // TODO - handle align
    return (h - this.netLineHeight) / 2;
  }

  alignVertically(dims: Coords, align?: 'left' | 'center' | 'right'): number {
    const [w] = dims;
    return (w - this.maxLineWidth) / 2;
  }

  scaleToHeight(height: number) {
    // -- //
  }

  updateRect() {
    const [width, height] = this.dim;
    //console.log('dims', { width, height });
    // this.rect?.patch({ width, height });
    this.rc?.patch({ width: width + 2 * RC_PADDING, height }, true);
  }

  contextIsUpdated(): void {
    this.refreshElement();
  }

  patch(key: string, value: any): void {
    super.patch(key, value);
    this.refreshElement();
  }

  _showCursor(position: { x: number; y: number }, word: TextWord) {}

  get grossLineHeight() {
    return this.lineHeight
      ? this.lineHeight * this.netLineHeight
      : this.netLineHeight;
  }
  get lineHeightOffset() {
    return (this.grossLineHeight - this.netLineHeight) / 2;
  }

  removeSelection() {
    // console.log('remove-selection');
    this.lines.map(line => line.removeSelectorRectangle());
    // this.updateSelectionIndexes(); //y
  }
}
